import { parseInstantFromApi } from "utils/dateFormatting";

import type { DocumentComment } from "./types";

export function parseCommentTimestampMs(iso: string | undefined): number {
  if (!iso) return 0;
  const t = parseInstantFromApi(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Latest activity in a thread (root + replies), using `created_at` / `updated_at`. */
export function latestThreadActivityMs(root: DocumentComment, replies: DocumentComment[]): number {
  let max = Math.max(
    parseCommentTimestampMs(root.created_at),
    parseCommentTimestampMs(root.updated_at)
  );
  for (const r of replies) {
    max = Math.max(
      max,
      parseCommentTimestampMs(r.created_at),
      parseCommentTimestampMs(r.updated_at)
    );
  }
  return max;
}

function commentSelfActivityMs(c: DocumentComment): number {
  return Math.max(parseCommentTimestampMs(c.created_at), parseCommentTimestampMs(c.updated_at));
}

/**
 * Latest activity from **other** participants (excludes the viewer’s own comments/edits).
 * Used so “unread” reflects messages you still need to see from collaborators, not your own sends.
 */
export function latestThreadActivityMsFromOthers(
  root: DocumentComment,
  replies: DocumentComment[],
  viewerUserId: string | undefined | null
): number {
  const viewer = viewerUserId?.trim();
  if (!viewer) {
    return latestThreadActivityMs(root, replies);
  }

  let max = 0;
  const all = [root, ...replies];
  for (const c of all) {
    const authorId = c.created_by_user_id?.trim();
    if (authorId && authorId === viewer) {
      continue;
    }
    max = Math.max(max, commentSelfActivityMs(c));
  }
  return max;
}

export function groupCommentsIntoThreads(comments: DocumentComment[]): {
  root: DocumentComment;
  replies: DocumentComment[];
}[] {
  const list = Array.isArray(comments) ? comments : [];
  const repliesByParent = new Map<string, DocumentComment[]>();
  const roots: DocumentComment[] = [];

  for (const comment of list) {
    if (comment.parent_comment_id) {
      const list = repliesByParent.get(comment.parent_comment_id) ?? [];
      list.push(comment);
      repliesByParent.set(comment.parent_comment_id, list);
    } else {
      roots.push(comment);
    }
  }

  return roots.map((root) => ({
    root,
    replies: repliesByParent.get(root.comment_id) ?? [],
  }));
}

/**
 * Thread is unread if baseline activity falls after the stored read time.
 * Baseline prefers **other** participants’ timestamps so your own replies are not surfaced as unread;
 * when nobody else participated, activity falls back to the full thread so “Mark as unread” still works.
 * Missing `readAt` ⇒ unread whenever there is qualifying baseline activity.
 * When `viewerUserId` is omitted, all thread activity counts (e.g. no signed-in user).
 */
export function isThreadUnreadByReadAt(
  root: DocumentComment,
  replies: DocumentComment[],
  readAtIso: string | undefined,
  viewerUserId?: string | null
): boolean {
  const viewer = viewerUserId?.trim();
  const othersMs = viewer
    ? latestThreadActivityMsFromOthers(root, replies, viewer)
    : latestThreadActivityMs(root, replies);
  const fullMs = latestThreadActivityMs(root, replies);
  const baselineMs = othersMs > 0 ? othersMs : fullMs;
  if (baselineMs <= 0) return false;
  if (readAtIso == null || readAtIso === "") return true;
  return baselineMs > parseCommentTimestampMs(readAtIso);
}

/** Header / toolbar: personal unread threads vs open (unresolved) threads for this screenplay. */
export function computeCommentHeaderBadge(
  comments: DocumentComment[],
  readAtByRoot: Record<string, string>,
  viewerUserId?: string | null
): { unreadThreadCount: number; unresolvedOpenThreadCount: number } {
  const threads = groupCommentsIntoThreads(comments);
  let unreadThreadCount = 0;
  let unresolvedOpenThreadCount = 0;
  for (const { root, replies } of threads) {
    if (root.status === "open") {
      unresolvedOpenThreadCount++;
    }
    if (isThreadUnreadByReadAt(root, replies, readAtByRoot[root.comment_id], viewerUserId)) {
      unreadThreadCount++;
    }
  }
  return { unreadThreadCount, unresolvedOpenThreadCount };
}
